import React from "react";
import {
  DollarSign, FileText, ShieldCheck, BarChart3,
} from "lucide-react";
import RichBlogLayout from "../../components/RichBlogLayout";
import {
  Prose, PullQuote, MetricsRow, BeforeAfter, ProcessFlow,
  FeatureCallout, AgentCard, FlowDiagram,
  StatBar, ChecklistCard, SectionDivider,
} from "../../components/blog/BlogGraphics";

const BlogNorahBankReconciliation = () => (
  <RichBlogLayout
    category="Revenue Cycle Management"
    eyebrow="Bank Reconciliation"
    title="Norah: Three-Way Bank Reconciliation, Continuous and to the Penny"
    subtitle="Plaid bank deposits, your PMS, and the 835 ERA — matched by trace ID, continuously, with no end-of-week spreadsheet."
    author="Asha Team"
    date="May 2, 2026"
    readTime="8 min read"
  >
    <Prose>
      <p>
        Ask the office manager of any busy practice when the last clean,
        full bank reconciliation happened, and watch the pause. Most practices
        run on a trio of records — what the carrier said it paid, what the
        bank actually deposited, and what the PMS shows on the patient
        ledger — and most of the time those three numbers do not perfectly
        agree. The deltas are usually small. They are also usually unexamined.
      </p>
      <p>
        Small unexamined deltas are exactly how revenue leaks. A $42 short on
        a Delta deposit. A capitation payment that landed under a different
        carrier name. A claim that posted but whose deposit never showed.
        Multiply that across hundreds of carrier deposits a month, every
        month, and the practice has a quiet five-figure annual leak that
        nobody has ever once put a number on.
      </p>
    </Prose>

    <AgentCard
      name="Norah"
      role="AI Bank Reconciliation · Plaid + ERA + PMS"
      initial="N"
      description="Norah continuously reconciles every carrier deposit against the 835 ERA and the PMS ledger — matched by trace ID, down to the penny. When something does not align, she flags it immediately with the full audit trail attached, so your team is not the one piecing together what went wrong."
      channels={["Plaid bank feed", "835 ERA matching", "PMS ledger sync", "Trace-ID variance flags"]}
    />

    <Prose>
      <h2>The three numbers that should always agree</h2>
      <p>
        Reconciliation is, at its core, a triangle. The bank tells you a
        certain dollar amount landed in your account on a given day. The 835
        ERA from the carrier tells you that same dollar amount was paid out
        across a specific list of claims. Your PMS tells you which of those
        claim payments were posted and credited to which patient ledger. When
        all three lines up, the books are clean. When any one of them
        disagrees, money is unaccounted for.
      </p>
    </Prose>

    <MetricsRow
      items={[
        { value: 428, label: "Transactions matched this month" },
        { value: 100, suffix: "%", label: "Reconciled to the penny" },
        { value: 0, label: "End-of-month spreadsheets" },
        { value: 3, suffix: "-way", label: "Bank · ERA · PMS match" },
      ]}
    />

    <Prose>
      <p>
        The conventional way to enforce that triangle is a once-a-month
        reconciliation by an office manager or an outside billing service.
        That works in the sense that most months close. It does not work in
        the sense that variances discovered at month-end are already weeks
        old and increasingly hard to chase.
      </p>

      <h2>Continuous, not month-end</h2>
      <p>
        The most important shift Norah introduces is not the matching itself —
        it's the cadence. Reconciliation stops being a monthly fire drill and
        becomes something that happens line by line, the moment a deposit
        hits the account.
      </p>
    </Prose>

    <BeforeAfter
      beforeTitle="Month-end reconciliation"
      afterTitle="With Norah"
      beforeItems={[
        "Office manager pulls bank statements at month-end",
        "Cross-checks against ERA stack and PMS posting reports manually",
        "Variances are weeks old and the audit trail is cold",
        "'Close enough' becomes the standard when the spreadsheet won't tie out",
      ]}
      afterItems={[
        "Plaid pulls real bank deposits in near real time",
        "Each deposit matched against the 835 ERA and PMS ledger by trace ID",
        "Variances flagged the day they appear, with full context",
        "100% of deposits reconciled to the penny — continuously, not at month-end",
      ]}
    />

    <Prose>
      <h2>The three-way match, visualized</h2>
      <p>
        For every deposit, Norah holds three records side by side and looks
        for the trace identifier that ties them together. When all three
        agree, the line is closed. When they don't, the variance is exactly
        what shows up in the team's morning queue.
      </p>
    </Prose>

    <FlowDiagram
      title="Norah's three-way match"
      nodes={[
        { label: "Bank deposit", sublabel: "Plaid · live feed", icon: DollarSign },
        { label: "835 ERA", sublabel: "Carrier remittance", icon: FileText },
        { label: "PMS ledger", sublabel: "Posted payments", icon: BarChart3 },
        { label: "Trace-ID match", sublabel: "Reconciled to the penny", icon: ShieldCheck },
      ]}
    />

    <Prose>
      <h2>How a single deposit moves through Norah</h2>
      <p>
        A new deposit lands at 9:14 AM. The carrier ERA arrived in the
        clearinghouse twenty minutes earlier. Alicia has already posted the
        line items into the PMS ledger. The reconciliation triangle is now
        complete and Norah's job is to verify it.
      </p>
    </Prose>

    <ProcessFlow
      steps={[
        {
          title: "Pulls the deposit from the bank in real time",
          body:
            "Through a secure Plaid connection, Norah sees the deposit the moment it clears — carrier, amount, and trace identifier — without anyone exporting a CSV from the bank portal.",
        },
        {
          title: "Locates the matching 835 ERA",
          body:
            "Norah finds the carrier remittance file that corresponds to that trace ID and the dollar amount, and unpacks the line-item breakdown.",
        },
        {
          title: "Cross-checks the PMS ledger",
          body:
            "She confirms the posted line items in the PMS match the ERA's allocation across patients and claims, dollar for dollar.",
        },
        {
          title: "Verifies the math down to the penny",
          body:
            "Bank deposit equals the sum of ERA allocations equals the sum of posted ledger entries. If a single penny is off, the line does not close.",
        },
        {
          title: "Flags variance with the full audit trail",
          body:
            "Mismatch surfaces in the team's queue with the bank line, the ERA line, and the PMS line side by side — not as a cryptic delta to investigate from scratch.",
        },
      ]}
    />

    <PullQuote>
      No mystery deltas, no manual reconciliation backlog, no revenue quietly slipping through the cracks.
    </PullQuote>

    <Prose>
      <h2>One reconciliation, six checks</h2>
      <p>
        Take a single Delta Dental EFT deposit of $4,824.10 that lands on a
        Wednesday morning. By the time anyone in the office looks at the
        Plaid feed, Norah has already worked through the full audit.
      </p>
    </Prose>

    <ChecklistCard
      title="Reconciliation · Delta Dental EFT $4,824.10"
      items={[
        { label: "Bank deposit observed via Plaid · trace ID 8847291", status: "pass" },
        { label: "Matching 835 ERA located · 14 claim lines", status: "pass" },
        { label: "ERA line-item sum equals deposit ($4,824.10)", status: "pass" },
        { label: "All 14 claims posted in PMS ledger by Alicia", status: "pass" },
        { label: "Posted ledger sum equals ERA total", status: "pass" },
        { label: "Patient responsibility splits routed to Rachel", status: "pass" },
      ]}
    />

    <Prose>
      <h2>The variance dashboard that finally exists</h2>
      <p>
        Because Norah runs continuously, the office now has something that
        most practices have never had: a real, daily view of reconciliation
        accuracy by carrier. The number that used to be "we think we're fine"
        becomes a verified percentage with named exceptions.
      </p>
    </Prose>

    <StatBar
      title="Carrier reconciliation accuracy · this month"
      rows={[
        { label: "Delta Dental", value: 142, max: 142 },
        { label: "Aetna", value: 86, max: 86 },
        { label: "Cigna", value: 71, max: 72, suffix: " · 1 flagged" },
        { label: "MetLife", value: 54, max: 54 },
        { label: "Guardian", value: 41, max: 41 },
        { label: "UnitedHealthcare", value: 33, max: 33 },
      ]}
    />

    <FeatureCallout
      stat="100%"
      label="Of carrier deposits reconciled to the penny — continuously"
      description="Plaid feeds the bank, the 835 ERA tells the carrier's story, the PMS ledger holds the posting truth. Norah ties all three to a single trace ID, every single line."
      icon={ShieldCheck}
    />

    <Prose>
      <h2>What month-end becomes</h2>
      <p>
        Month-end stops being a panic. The office manager who used to spend a
        day with a spreadsheet now spends fifteen minutes reviewing the
        exception queue. The variances that used to get rounded into "close
        enough" become a small, finite list of items to chase — and because
        they are caught the day they appear, they are still chaseable.
      </p>
      <p>
        From the practice owner's seat, the most quietly important shift is
        the existence of the audit trail. Every dollar received has a clear
        line back to a specific carrier deposit, a specific ERA line, and a
        specific posted claim. That is not just a back-office nicety. It is
        the foundation of every other revenue cycle decision the practice
        makes.
      </p>
    </Prose>

    <SectionDivider label="Bottom line" />

    <Prose>
      <p>
        Bank reconciliation is the part of the back office most practices have
        decided to live with as a recurring chore. Norah turns it into a
        continuous three-way match — trace ID by trace ID, down to the penny.
        The leaks that used to be invisible become a managed list of
        exceptions, and month-end stops being a fire drill.
      </p>
    </Prose>
  </RichBlogLayout>
);

export default BlogNorahBankReconciliation;
